import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { FolderKanban, ListChecks, Search, X } from 'lucide-react';

import { useSearch } from '../lib/search';
import { t } from '../lib/i18n';
import { Spinner } from './ui';

/**
 * SearchDialog — buscador global (paleta de comandos). Input arriba + resultados
 * en vivo (proyectos y tareas), cada uno enlaza a su tablero. Se abre desde la
 * topbar o con Ctrl/⌘+K.
 */
export function SearchDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [input, setInput] = useState('');
  const [debounced, setDebounced] = useState('');
  const navigate = useNavigate();

  // Debounce del texto para no pegarle al backend en cada tecla.
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(input), 220);
    return () => window.clearTimeout(id);
  }, [input]);

  // Reset al cerrar.
  useEffect(() => {
    if (!open) {
      setInput('');
      setDebounced('');
    }
  }, [open]);

  const q = useSearch(debounced);
  const data = q.data;
  const hasQuery = debounced.trim().length >= 2;
  const empty =
    hasQuery && !q.isLoading && !!data && data.projects.length === 0 && data.tasks.length === 0;

  const go = (to: string) => {
    onOpenChange(false);
    navigate(to);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="overlay-in fixed inset-0 z-50 bg-ink/50 backdrop-blur-[2px]" />
        <Dialog.Content
          aria-describedby={undefined}
          className="hero-rise fixed left-1/2 top-[12vh] z-50 flex max-h-[70vh] w-[min(560px,calc(100vw-2rem))] -translate-x-1/2 flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-lift focus:outline-none"
        >
          <Dialog.Title className="sr-only">{t('search.title')}</Dialog.Title>

          {/* Input */}
          <div className="flex items-center gap-2 border-b border-line px-4 py-3">
            <Search size={18} className="shrink-0 text-muted" />
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('search.placeholder')}
              className="flex-1 bg-transparent text-sm text-ink placeholder:text-muted focus:outline-none"
            />
            {q.isFetching && hasQuery && <Spinner className="h-4 w-4" />}
            <Dialog.Close
              aria-label={t('search.close')}
              className="grid h-7 w-7 shrink-0 place-items-center rounded-token text-muted transition-colors hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <X size={16} />
            </Dialog.Close>
          </div>

          {/* Resultados */}
          <div className="flex-1 overflow-y-auto p-2">
            {!hasQuery && <p className="px-3 py-6 text-center text-sm text-muted">{t('search.hint')}</p>}
            {empty && <p className="px-3 py-6 text-center text-sm text-muted">{t('search.empty')}</p>}

            {data && data.projects.length > 0 && (
              <div className="mb-2">
                <p className="px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted">
                  {t('search.projects')}
                </p>
                {data.projects.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => go(`/proyectos/${p.id}`)}
                    className="flex w-full items-center gap-2.5 rounded-token px-3 py-2 text-left text-sm text-ink transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none"
                  >
                    <FolderKanban size={16} className="shrink-0 text-brand" />
                    <span className="min-w-0 flex-1 truncate">{p.name}</span>
                    {p.folder_name && <span className="shrink-0 text-xs text-muted">{p.folder_name}</span>}
                  </button>
                ))}
              </div>
            )}

            {data && data.tasks.length > 0 && (
              <div>
                <p className="px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted">
                  {t('search.tasks')}
                </p>
                {data.tasks.map((tk) => (
                  <button
                    key={tk.id}
                    type="button"
                    onClick={() => go(`/proyectos/${tk.project_id}?task=${tk.id}`)}
                    className="flex w-full items-start gap-2.5 rounded-token px-3 py-2 text-left transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none"
                  >
                    <ListChecks size={16} className="mt-0.5 shrink-0 text-muted" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-ink">{tk.title}</span>
                      <span className="block truncate text-xs text-muted">
                        {tk.project_name}
                        {tk.column_name ? ` · ${tk.column_name}` : ''}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
